import React, { useMemo, useState } from 'react';
import Alert from '@cloudscape-design/components/alert';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Header from '@cloudscape-design/components/header';
import Multiselect, { MultiselectProps } from '@cloudscape-design/components/multiselect';
import Select from '@cloudscape-design/components/select';
import SpaceBetween from '@cloudscape-design/components/space-between';
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { api } from '../api/client';
import { useNotifications } from '../hooks/useNotifications';
import { optionsStatusProps, useOptions } from '../hooks/useOptions';
import { usePolicyList, useSubmit } from '../hooks/usePolicyList';
import { useActionDelete } from '../hooks/useActionDelete';
import { ResultViewer } from '../components/ResultViewer';
import { PolicyModal } from '../components/PolicyModal';
import { deleteColumn } from '../components/deleteColumn';
import { ActionChecklist } from '../components/ActionChecklist';
import {
  USER_GROUP_RESOURCE_ACTION_COLUMNS,
  UserGroupResourceActionRow,
  resourceOptions,
  toNamedOptions,
  toResourceChoices,
  toUserGroupResourceActionRows,
} from './policyRows';

export const UserGroupResourceActionPage: React.FC<{ app: string }> = ({ app }) => {
  const { base, loading, result, reload } = usePolicyList(app, 'user_group_resource_action');
  const { addNotification } = useNotifications();
  const rows = useMemo(() => toUserGroupResourceActionRows(result?.data), [result]);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [groupName, setGroupName] = useState('');
  const [resourcePath, setResourcePath] = useState('');
  const [actions, setActions] = useState<readonly MultiselectProps.Option[]>([]);
  const groups = useOptions(`/${encodeURIComponent(app)}/user_groups`, toNamedOptions);
  const resources = useOptions(`/${encodeURIComponent(app)}/resources`, toResourceChoices);
  const actionList = useOptions(`/${encodeURIComponent(app)}/actions`, toNamedOptions);
  const create = useSubmit();

  // Delete confirmation
  const del = useActionDelete<UserGroupResourceActionRow>(
    (r, name) => api('DELETE', base, { user_group: { name: r.userGroup }, resource: r.resource, action: { name } }),
    async (r, removed) => {
      addNotification('success', `Removed ${removed.join(', ')} on ${r.resourcePath} from ${r.userGroup}`);
      await reload();
    }
  );

  const columns = useMemo(
    () => [
      ...USER_GROUP_RESOURCE_ACTION_COLUMNS,
      deleteColumn<UserGroupResourceActionRow>(
        r => (r.userGroup && r.resource && r.actions.length ? `${r.userGroup} on ${r.resourcePath}` : null),
        r => del.open(r)
      ),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- open only calls state setters
    []
  );

  if (!app) return <Alert type="info">Select an application from the top bar.</Alert>;

  const openCreate = () => {
    setGroupName('');
    setResourcePath('');
    setActions([]);
    create.setError(null);
    setCreateOpen(true);
    groups.load();
    resources.load();
    actionList.load();
  };

  const confirmCreate = async () => {
    const resource = resources.state.items.find(c => c.path === resourcePath)?.resource;
    const names = actions.map(a => a.value!);
    const ok = await create.submit(() =>
      api('PUT', base, {
        user_groups: [{ name: groupName }],
        resources_with_actions: [{ resource, actions: names.map(name => ({ name })) }],
      })
    );
    if (ok) {
      setCreateOpen(false);
      addNotification('success', `Granted ${names.join(', ')} on ${resourcePath} to ${groupName}`);
      await reload();
    }
  };

  const selectedGroup = groupName
    ? groups.state.items.find(o => o.value === groupName) ?? { value: groupName, label: groupName }
    : null;
  const selectedResource = resourcePath ? { value: resourcePath, label: resourcePath } : null;
  const canCreate = groupName !== '' && resourcePath !== '' && actions.length > 0;

  return (
    <SpaceBetween size="l">
      <Header
        variant="h1"
        actions={
          <Button variant="primary" onClick={openCreate}>
            Create Policy
          </Button>
        }
      >
        UserGroup - Resource - Action Policy
      </Header>

      {loading && !result ? (
        <StatusIndicator type="loading">Loading policies</StatusIndicator>
      ) : (
        <ResultViewer data={result?.data} error={result?.error} rows={rows} columns={columns} title="Policy" />
      )}

      <PolicyModal
        visible={createOpen}
        header="Create policy"
        confirmText="Create"
        busy={create.busy}
        confirmDisabled={!canCreate}
        error={create.error}
        errorHeader="Create failed"
        onCancel={() => setCreateOpen(false)}
        onConfirm={confirmCreate}
      >
        <FormField label="User Group">
          <Select
            selectedOption={selectedGroup}
            onChange={({ detail }) => setGroupName(detail.selectedOption.value ?? '')}
            options={groups.state.items}
            {...optionsStatusProps(groups.state, 'user groups')}
            placeholder="Choose a user group"
            ariaLabel="User Group"
          />
        </FormField>
        <FormField label="Resource">
          <Select
            selectedOption={selectedResource}
            onChange={({ detail }) => setResourcePath(detail.selectedOption.value ?? '')}
            options={resourceOptions(resources.state.items)}
            {...optionsStatusProps(resources.state, 'resources')}
            placeholder="Choose a resource"
            ariaLabel="Resource"
          />
        </FormField>
        <FormField label="Actions">
          <Multiselect
            selectedOptions={actions}
            onChange={({ detail }) => setActions(detail.selectedOptions)}
            options={actionList.state.items}
            {...optionsStatusProps(actionList.state, 'actions')}
            placeholder="Choose actions"
            ariaLabel="Actions"
          />
        </FormField>
      </PolicyModal>

      <PolicyModal
        visible={del.pending !== null}
        header="Delete policy"
        confirmText="Delete"
        busy={del.busy}
        confirmDisabled={del.toRemove.length === 0}
        error={del.error}
        errorHeader="Delete failed"
        onCancel={del.close}
        onConfirm={del.confirm}
      >
        {del.pending && (
          <>
            <Box>
              Remove actions on <b>{del.pending.resourcePath}</b> from user group <b>{del.pending.userGroup}</b> in{' '}
              <b>{app}</b>?
            </Box>
            <ActionChecklist
              actions={del.pending.actions}
              selected={del.toRemove}
              onChange={del.setToRemove}
              disabled={del.busy}
            />
          </>
        )}
      </PolicyModal>
    </SpaceBetween>
  );
};
